import { useMutation } from '@apollo/client';
import EditActionButton from '@components/EditActionButton';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_TALENT, UPDATE_CONTACTS_TALENT } from '@gql/talents';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { SectionCard } from './SectionCard';
import TalentContactsForm from './TalentContactsForm';
import { SectionTitle } from './SectionTitle';

type ContactItem = {
    icon: string;
    text: string;
};

interface TalentContactsCardProps {
    talent: any;
    onUpdated?: () => void;
}

const TalentContactsCard = ({ talent, onUpdated }: TalentContactsCardProps) => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [isEditMode, setIsEditMode] = useState(false);

    const [updateContacts, { loading }] = useMutation(UPDATE_CONTACTS_TALENT);

    const handleSubmit = async values => {
        try {
            await updateContacts({
                variables: {
                    talentId: talent?._id,
                    payload: values,
                },
                refetchQueries: [
                    {
                        query: GET_TALENT,
                        variables: { talentId: talent?._id },
                    },
                ],
            });

            showSnackbar('Coordonnees mises a jour avec succes', 'success');
            setIsEditMode(false);
            onUpdated?.();
        } catch (_error) {
            showSnackbar('Erreur lors de la mise a jour des coordonnees', 'error');
        }
    };

    const contacts: ContactItem[] = [
        talent.email && { icon: 'fi-br-envelope', text: talent.email },
        talent.social?.website && { icon: 'fi-br-browser', text: talent.social.website },
        talent.phoneNumber && { icon: 'fi-br-smartphone', text: talent.phoneNumber },
        talent.social?.instagram && { icon: 'fi-brands-instagram', text: talent.social.instagram },
        talent.social?.linkedin && { icon: 'fi-brands-linkedin', text: talent.social.linkedin },
        talent.social?.tiktok && { icon: 'fi-br-clip', text: talent.social.tiktok },
    ].filter((contact): contact is ContactItem => Boolean(contact));

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: 0, overflow: "hidden", background: theme.palette.dashboard.main }}>
            <Box
                sx={{
                    paddingY: 0,
                    overflow: 'hidden',
                    flex: 1,
                }}>
                <Box
                    sx={{
                        backgroundColor: theme.palette.transparentPurple.main,
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <SectionTitle title="Coordonnées" />

                    {!isEditMode && (
                        <EditActionButton
                            onClick={() => setIsEditMode(true)}
                        />
                    )}
                </Box>

                {isEditMode ? (
                    <Box sx={{ padding: 2 }}>
                        <TalentContactsForm
                            talent={talent}
                            loading={loading}
                            onCancel={() => setIsEditMode(false)}
                            onSubmit={handleSubmit}
                        />
                    </Box>
                ) : (
                    <Stack sx={{ padding: 2 }} gap={1}>
                        {contacts.map((contact, index) => (
                            <Box
                                key={`${contact.icon}-${index}`}
                                sx={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                }}>
                                <i
                                    className={`fi ${contact.icon}`}
                                    style={{ fontSize: '16px', color: theme.palette.neutralWhite.main }}
                                />
                                <Typography variant="body2" fontSize="13px" fontWeight={500}>
                                    {contact.text}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionCard>
    );
};

export default TalentContactsCard;
